// provider.store.ts
import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import {
  addEntities,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { exhaustMap, pipe, switchMap, tap } from 'rxjs';
import { ProviderService } from '../../../core/services/provider/provider.service';
import { AdminService } from './admin.service';
import { DataStatus, Provider, StoreError } from '../models/models';

interface ProviderState {
  providerStatus: DataStatus;
  activeClinicId: number;
  error: StoreError;
}

export const ProviderStore = signalStore(
  { providedIn: 'root', protectedState: false },

  withState<ProviderState>({
    providerStatus: 'idle',
    activeClinicId: 0,
    error: { error: false, message: '' },
  }),

  withEntities<Provider>(),

  withProps(() => ({
    adminService: inject(AdminService),
    providerService: inject(ProviderService),
  })),

  withComputed((store) => ({
    providerList: computed(() => store.entities()),

    providersForActiveClinic: computed(() =>
      store.entities().filter((p) => p.clinic_id === store.activeClinicId())
    ),

    providersByClinicMap: computed(() => {
      const map = new Map<number, Provider[]>();

      for (const provider of store.entities()) {
        const current = map.get(provider.clinic_id) ?? [];
        current.push(provider);
        map.set(provider.clinic_id, current);
      }

      return map;
    }),
  })),

  withMethods((store) => ({
    setProviderStatus(providerStatus: DataStatus) {
      patchState(store, { providerStatus });
    },

    setActiveClinicId(activeClinicId: number) {
      patchState(store, { activeClinicId });
    },

    setError(message: string) {
      patchState(store, {
        providerStatus: 'error',
        error: { error: true, message },
      });
    },

    clearError() {
      patchState(store, {
        error: { error: false, message: '' },
      });
    },

    getProvidersByClinicId(clinicId: number): Provider[] {
      return store.providersByClinicMap().get(clinicId) ?? [];
    },
    updateProviderOnboardingStatus(providerId: number, onboarding_status: number) {
      patchState(store, updateEntity({
        id: providerId,
        changes: {
            onboarding_status
        }
      }));
    },
  })),

  withMethods((store) => ({
    loadAllProviders: rxMethod<void>(
      pipe(
        tap(() => {
          store.clearError();
          store.setProviderStatus('loading');
        }),
        switchMap(() =>
          store.providerService.getAllProviders().pipe(
            tapResponse({
              next: (providers: any) => {
                patchState(store, addEntities(providers));
                store.setProviderStatus('success');
              },
              error: (err: unknown) => {
                console.error(err);
                store.setError('Failed to load providers.');
              },
            })
          )
        )
      )
    ),

    loadProvidersByClinic: rxMethod<number>(
      pipe(
        tap(() => {
          store.clearError();
          store.setProviderStatus('loading');
        }),
        switchMap((clinic_id) =>
          store.providerService.getAllProidersByClinic(clinic_id).pipe(
            tapResponse({
              next: (providers: Provider[]) => {
                const normalized = providers.map((provider) => ({
                  ...provider,
                  clinic_id,
                }));

                patchState(store, addEntities(normalized));
                store.setProviderStatus('success');
              },
              error: (err: unknown) => {
                console.error(err);
                store.setError('Failed to load clinic providers.');
              },
            })
          )
        )
      )
    ),
    activateProvider: rxMethod<{clinic_id: number, provider_id: number}>(
      pipe(
        tap(() => {
          store.clearError()
          store.setProviderStatus('loading')
        }),
        exhaustMap(({clinic_id, provider_id}) => 
          store.providerService.activateProvider(clinic_id, provider_id).pipe(
            tapResponse({
              next: () => {
                store.setProviderStatus('success');
              },
              error: (err: unknown) => {
                console.error(err);
                store.setError('Failed to load clinic providers.');
              }
            })
          )
        )
      )
    ),
    deactivateProvider: rxMethod<{clinic_id: number, provider_id: number, onSuccess?: () => void}>(
      pipe(
        tap(() => {
          store.clearError()
          store.setProviderStatus('loading')
        }),
        exhaustMap(({clinic_id, provider_id, onSuccess}) => 
          store.providerService.deactivateProvider(clinic_id, provider_id).pipe(
            tapResponse({
              next: () => {
                store.setProviderStatus('success')
                patchState(store, updateEntity({
                  id: provider_id,
                  changes: {
                      active: false
                  }
                }));
                onSuccess?.()
              },
              error: (err: unknown) => {
                store.setProviderStatus('error')
                store.setError('Failed to deactivate the provider')
              }
            })
          )
        )
      )
    )
  }))
);